import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Upload, X, FileText, Image } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '', content: '', excerpt: '', image_url: '', published: true, attachments: []
  });
  const [uploadingAtt, setUploadingAtt] = useState(false);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog?published=false`);
      setPosts(response.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/blog/${editingId}`, formData, { withCredentials: true });
        toast.success('Article mis à jour');
      } else {
        await axios.post(`${API}/blog`, formData, { withCredentials: true });
        toast.success('Article créé');
      }
      resetForm();
      fetchPosts();
    } catch (error) { toast.error('Erreur'); }
  };

  const handleEdit = (post) => {
    setEditingId(post.post_id);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt,
      image_url: post.image_url || '',
      published: post.published,
      attachments: post.attachments || []
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await axios.delete(`${API}/blog/${id}`, { withCredentials: true });
      toast.success('Article supprimé');
      fetchPosts();
    } catch (error) { toast.error('Erreur'); }
  };

  const resetForm = () => {
    setFormData({ title: '', content: '', excerpt: '', image_url: '', published: true, attachments: [] });
    setEditingId(null);
    setShowForm(false);
  };

  const handleUploadAttachment = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 20 Mo');
      return;
    }
    setUploadingAtt(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/files/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const isImage = file.type.startsWith('image/');
      const isPdf = file.type === 'application/pdf';
      const fileType = isImage ? 'image' : (isPdf ? 'pdf' : 'document');
      setFormData((f) => ({
        ...f,
        attachments: [...f.attachments, {
          title: file.name,
          url: res.data.url,
          file_type: fileType,
          file_size: file.size
        }]
      }));
      toast.success('Pièce jointe ajoutée');
    } catch (err) { toast.error('Erreur lors du chargement'); }
    finally { setUploadingAtt(false); e.target.value = ''; }
  };

  const removeAttachment = (idx) => {
    setFormData((f) => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));
  };

  return (
    <div data-testid="blog-manager">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Gestion du Blog ({posts.length})
        </h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary inline-flex items-center" data-testid="add-blog-button">
          <Plus size={20} className="mr-2" />{showForm ? 'Annuler' : 'Ajouter'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card mb-6">
          <div className="space-y-4">
            <input type="text" placeholder="Titre" required value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
            <input type="text" placeholder="Résumé (extrait)" required value={formData.excerpt} onChange={(e) => setFormData({...formData, excerpt: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
            <textarea placeholder="Contenu complet de l'article" required rows={10} value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg font-mono text-sm" />
            <input type="url" placeholder="URL image de couverture (optionnel)" value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />

            {/* Attachments */}
            <div className="border-2 border-dashed border-[#E5E3DB] rounded-lg p-4">
              <label className="flex items-center gap-2 text-sm font-bold text-[#2E332D] mb-2">
                <Upload size={16} /> Pièces jointes (PDF, images, documents — max 20 Mo)
              </label>
              <input type="file" accept=".pdf,.doc,.docx,image/*" onChange={handleUploadAttachment} disabled={uploadingAtt} className="text-sm" data-testid="blog-attachment-upload" />
              {uploadingAtt && <p className="text-xs text-[#5C665A] mt-1">Chargement...</p>}
              {formData.attachments.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {formData.attachments.map((a, idx) => (
                    <li key={`${a.url}-${idx}`} className="flex items-center justify-between gap-2 bg-[#F9F8F6] p-2 rounded text-sm">
                      <span className="inline-flex items-center gap-2 truncate text-[#2E332D]">
                        {a.file_type === 'image' ? <Image size={14} className="text-[#D87D4A]" /> : <FileText size={14} className="text-[#4A6B48]" />}
                        <span className="truncate">{a.title}</span>
                      </span>
                      <button type="button" onClick={() => removeAttachment(idx)} className="text-xs text-red-500 hover:underline shrink-0">
                        <X size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.published} onChange={(e) => setFormData({...formData, published: e.target.checked})} />
              <span className="text-sm text-[#2E332D]">Publier immédiatement</span>
            </label>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary">{editingId ? 'Mettre à jour' : 'Créer'}</button>
              <button type="button" onClick={resetForm} className="btn-secondary">Annuler</button>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6B48]"></div></div>
      ) : (
        <div className="space-y-4">
          {posts.length === 0 ? (
            <p className="text-center text-[#5C665A] py-8">Aucun article. Créez le premier article.</p>
          ) : posts.map((post) => (
            <div key={post.post_id} className="card">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`text-xs font-medium px-2 py-1 rounded ${post.published ? 'text-[#4A6B48] bg-[#4A6B48]/10' : 'text-orange-600 bg-orange-100'}`}>
                      {post.published ? 'Publié' : 'Brouillon'}
                    </span>
                    <span className="text-xs text-[#5C665A]">par {post.author}</span>
                  </div>
                  <h3 className="text-lg font-bold text-[#2E332D]">{post.title}</h3>
                  <p className="text-sm text-[#5C665A] mt-1">{post.excerpt}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button onClick={() => handleEdit(post)} className="p-2 hover:bg-gray-100 rounded"><Edit2 size={18} className="text-[#4A6B48]" /></button>
                  <button onClick={() => handleDelete(post.post_id)} className="p-2 hover:bg-gray-100 rounded"><Trash2 size={18} className="text-red-500" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
